/* eslint-disable */
import React from "react";
import { Hexagon, SlidersHorizontal } from "lucide-react";
import { MdCalendarMonth } from "react-icons/md";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import ProjectionsIcon from "app/assets/IconComponent/ProjectionsIcon";
import AssetValueChartFilter from "v2/features/plans/components/AssetValueChartFilter";
import AssetValueLineChart from "v2/features/plans/components/AssetValueLineChart";

export type PerformanceAssetLineChartData = NonNullable<
  React.ComponentProps<typeof AssetValueLineChart>["data"]
>;

export interface PerformanceChartPanelProps {
  isDark: boolean;
  cardBg: string;
  divider: string;
  textMain: string;
  textSub: string;
  insuredName: string;
  chartBusy: boolean;
  graphDataLength: number;
  performanceLoading: boolean;
  assetLineChartData: PerformanceAssetLineChartData;
  assetChartFilter: string;
  onAssetChartFilterChange: (v: string) => void;
  chartSeriesFilters: string[];
  showProjections: boolean;
  onProjectionsToggle: () => void;
  projectionsIconFill: string;
  combinedFirstLineExtras: Record<string, unknown> | undefined;
  trueIqAppendSeries: object[];
  chartGridTop: string;
  trueIqChartMode: boolean;
}

const PerformanceChartPanel = React.memo((props: PerformanceChartPanelProps) => {
  const {
    isDark,
    cardBg,
    divider,
    textMain,
    textSub,
    insuredName,
    chartBusy,
    graphDataLength,
    performanceLoading,
    assetLineChartData,
    assetChartFilter,
    onAssetChartFilterChange,
    chartSeriesFilters,
    showProjections,
    onProjectionsToggle,
    projectionsIconFill,
  } = props;
  return (
    <div
      className={`rounded-2xl overflow-hidden border shadow-sm ${
        isDark ? "border-[#3A3A50] shadow-none" : "border-slate-200/90 shadow-md"
      } ${cardBg}`}
    >
      <div className={`flex items-center justify-between px-4 py-3 border-b ${divider}`}>
        <span className={`text-[15px] font-semibold tracking-tight ${textMain}`}>
          {/* Performance Chart */}
        </span>
        <div className="flex items-center gap-0.5">
          <Button
            type="button"
            text
            className="p-2"
            icon={<Hexagon size={18} className={textSub} />}
            aria-label="Insights"
          />
          <Button
            type="button"
            text
            className="p-2"
            icon={<SlidersHorizontal size={18} className={textSub} />}
            aria-label="Chart settings"
          />
          <Button
            type="button"
            text
            className="p-2 focus:outline-none focus:ring-0"
            icon={<ProjectionsIcon key={`proj-${showProjections}`} fill={projectionsIconFill} />}
            onClick={onProjectionsToggle}
            tooltip={
              showProjections
                ? "Hide projections (actual only)"
                : "Show projections (actual + projected)"
            }
            tooltipOptions={{ position: "top" }}
            aria-label="Toggle projected series"
          />
        </div>
      </div>

      <div className="px-1 sm:px-2 pt-2 pb-0 relative w-full min-w-0">
        {chartBusy && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/40">
            <ProgressSpinner style={{ width: 42, height: 42 }} />
          </div>
        )}
        <div className={`h-full w-full rounded-xl min-w-0 ${isDark ? "bg-[#14141c]" : "bg-white"}`}>
          <AssetValueLineChart
            data={assetLineChartData}
            loading={performanceLoading && !graphDataLength}
            height="365px"
            className="asset-chart w-full min-w-0"
            selectedChartFilter={assetChartFilter}
            selectedFilters={chartSeriesFilters}
            showProjections={showProjections}
            useBrandingColors
            // hidePlotAxisBaseline
            // firstLineSeriesExtras={combinedFirstLineExtras}
            // appendSeries={trueIqAppendSeries}
            // chartGridTop={chartGridTop}
            // chartGridLeft="1%"
            // chartGridRight={0}
            // chartContainerPadding="4px 4px 2px"
            // omitProjectedSeries={trueIqChartMode && showProjections}
          />

          <div className="mt-1 px-1 pb-2">
            <AssetValueChartFilter
              selectedChartFilter={assetChartFilter}
              onChartFilterChange={onAssetChartFilterChange}
              showProjections={showProjections}
              onProjectionsToggle={onProjectionsToggle}
              enableAgeFilter
            />
          </div>
        </div>
      </div>

      <div className={`flex items-center px-4 pb-3 gap-2 border-t ${divider}`}>
        <MdCalendarMonth size={18} className={`flex-shrink-0 ${textSub}`} />
        <p className={`text-[12px] text-center flex-1 ${textSub}`}>
          Plan Funding for {insuredName}
        </p>
      </div>
    </div>
  );
});

PerformanceChartPanel.displayName = "PerformanceChartPanel";

export default PerformanceChartPanel;
